import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Vector3 } from 'three';

import { isDefined } from '../../../../../utils/variables';
import { AssetManager } from '../../../../../viewer/asset-manager';
import cameraManager from '../../../../../viewer/camera-manager';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import { Design } from '../../../../../viewer/elements/design';
import { Dxf } from '../../../../../viewer/elements/dxf';
import { ElementObj } from '../../../../../viewer/elements/element-obj';
import { InputHandler } from '../../../../../viewer/input-handler';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { saveAssetTransformation } from '../../../../../viewer/tools/transform-tool';
import { Coordinates, Point3d } from '../../../../../viewer/utilities/math-helpers';
import { useButtonHotkey } from '../../../../hooks/use-button-hotkey';
import { useNonce } from '../../../../hooks/use-nonce';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { ProjectSpatialReferenceContext } from '../../../../state/project-reference-state';
import { useOptionalUserState } from '../../../../state/user-state/user-state';
import { Button } from '../../../button/button';
import { Position } from '../../../coordinates/position';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { CoordinateValidationInput } from '../../../skyview/right-menu/tools/reference-point-transform/coordinate-validation-input';
import { canAssetBeTransformedWithReferencePointTool } from '../../../skyview/right-menu/tools/reference-point-transform/reference-point-transform';
import { Stack } from '../../../stack/stack';

const SetAssetCoordinate = () => {
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const [offset, setOffset] = useState(new Vector3(0, 0, 0));
  const [target, setTarget] = useState<Coordinates>({ easting: 0, height: 0, northing: 0 });
  const [originalValue, setOriginalValue] = useState<Point3d>({ x: 0, y: 0, z: 0 });
  const [displayValue, setDisplayValue] = useState<Point3d>({ x: 0, y: 0, z: 0 });
  const [customTitle, setCustomTitle] = useState<string>();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>();
  const [hasAccess, setHasAccess] = useState(true);
  const [nonce, refreshCoordinateInput] = useNonce();

  const [asset, setAsset] = useState<ElementObj>();
  const [showDialog, setShowDialog] = useState(false);
  const [inputValid, setInputValid] = useState(true);

  const disabledMove = loading || !isDefined(asset) || asset.freeze.translate || !hasAccess;
  const canBeMoved = asset?.freeze.translate === false && hasAccess;
  const canBeScaled = canAssetBeTransformedWithReferencePointTool(asset);

  const updatePosition = useCallback(
    (value: Point3d) => {
      setDisplayValue(value);
      setTarget({
        northing: value.x,
        easting: value.y,
        height: value.z,
      });
      refreshCoordinateInput();
    },
    [refreshCoordinateInput],
  );

  const { getCoordinateSystemOrDefault } = React.useContext(ProjectSpatialReferenceContext);
  const coordinateSystem = useRef(getCoordinateSystemOrDefault());
  const confirmRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useButtonHotkey(confirmRef, 'Enter');
  useButtonHotkey(closeRef, 'Escape');

  useSubscribe(SubscriptionTopic.ShowSetAssetCoordinateDialog, (event) => {
    const currentWorldPosition = event.originWorldPosition ?? event.asset.position;

    /**
     * By calculating the offset based on the world positions it will be correct
     * even if the asset is scaled or rotated.
     */
    const offset = currentWorldPosition.clone().sub(event.asset.getWorldPosition(new Vector3()));

    const currentCoordinate =
      CoordinateManager.getInstance().localToProjectCoords(currentWorldPosition);

    setCustomTitle(event.title);
    setInfoMessage(event.infoMessage);
    setHasAccess(
      event.asset instanceof Design || event.asset instanceof Dxf
        ? userState?.hasDesignToolAccessForCurrentProject() ?? false
        : true,
    );
    setHasError(false);
    setAsset(event.asset);
    setOffset(offset);

    const initialValue = {
      x: currentCoordinate.northing,
      y: currentCoordinate.easting,
      z: currentCoordinate.height,
    };

    setOriginalValue(initialValue);
    setDisplayValue(initialValue);

    setShowDialog(true);
  });

  if (!showDialog) {
    return <></>;
  }

  return (
    <Dialog
      closeButtonRef={closeRef}
      closeIcon={true}
      closeOnDimmerClick={true}
      confirmButtonRef={confirmRef}
      width={600}
      onClose={() => setShowDialog(false)}
    >
      {{
        header: customTitle ?? t('transformTool.dialog.title', { ns: 'skyview' }),
        content: (
          <Stack spacing={1}>
            {(isDefined(infoMessage) || !canBeMoved) && (
              <InfoBox color="yellow">
                <Stack spacing={0.5}>
                  {isDefined(infoMessage) && <span>{infoMessage}</span>}
                  {!canBeMoved && <Position point={originalValue} />}
                </Stack>
              </InfoBox>
            )}

            {canBeMoved && (
              <>
                <LabelledContainer
                  text={t('transformTool.dialog.copyCoordinate', { ns: 'skyview' })}
                >
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      color="secondary"
                      disabled={disabledMove}
                      leftIcon={{ icon: ['fal', 'copy'] }}
                      variant="contained"
                      onClick={() => {
                        const lookPosition = CoordinateManager.getInstance().localToProjectCoords(
                          cameraManager().getActiveCamera()!.getLookAtPosition(),
                        );
                        updatePosition({
                          x: lookPosition.northing,
                          y: lookPosition.easting,
                          z: lookPosition.height,
                        });
                      }}
                    >
                      {t('transformTool.dialog.copyCoordinateFromCurrentView', { ns: 'skyview' })}
                    </Button>

                    <Button
                      color="secondary"
                      disabled={disabledMove}
                      leftIcon={{ icon: ['fal', 'copy'] }}
                      variant="contained"
                      onClick={() => {
                        const origo = CoordinateManager.getInstance().localToProjectCoords(
                          new Vector3(0, 0, 0),
                        );
                        updatePosition({
                          x: origo.northing + originalValue.x,
                          y: origo.easting + originalValue.y,
                          z: origo.height + originalValue.z,
                        });
                      }}
                    >
                      {t('transformTool.dialog.copyCoordinateRelativeToOrigo', { ns: 'skyview' })}
                    </Button>

                    <Button
                      color="secondary"
                      disabled={disabledMove}
                      leftIcon={{ icon: ['fal', 'copy'] }}
                      variant="contained"
                      onClick={() => {
                        updatePosition({
                          x: originalValue.x,
                          y: originalValue.y,
                          z: originalValue.z,
                        });
                      }}
                    >
                      {t('transformTool.dialog.copyOriginalCoordinate', { ns: 'skyview' })}
                    </Button>
                  </Stack>
                </LabelledContainer>

                <CoordinateValidationInput
                  coordinateSystem={coordinateSystem.current}
                  defaultValue={displayValue}
                  disabled={disabledMove}
                  key={nonce}
                  leftBorderColor="green"
                  showHeight={true}
                  onValidate={(valid) => setInputValid(valid)}
                  onValueChanged={(coordinate) => setTarget(coordinate)}
                />
              </>
            )}

            {hasError && (
              <InfoBox color="red">{t('transformTool.dialog.error', { ns: 'skyview' })}</InfoBox>
            )}
            {!canBeScaled && !hasAccess && (
              <InfoBox bottomMargin={true} color="red">
                {t('transformTool.dialog.noAccess', { ns: 'skyview' })}
              </InfoBox>
            )}
          </Stack>
        ),
        footer: {
          left: (
            <>
              {canBeScaled && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    setShowDialog(false);
                    AssetManager.instance.select(asset);
                    InputHandler.instance.setTool('reference-point-transform');
                  }}
                >
                  {t('contextMenu.selectTool.scale', { ns: 'skyview' })}
                </Button>
              )}

              {!canBeScaled && !hasAccess && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    setShowDialog(false);
                    publish(SubscriptionTopic.ShowDesignLicenseDialog);
                  }}
                >
                  {t('access.readMore', { ns: 'skyviewDesign' })}
                </Button>
              )}
            </>
          ),
          right: (
            <>
              {canBeMoved && (
                <Button
                  color="primary"
                  disabled={disabledMove || !inputValid}
                  loading={loading}
                  ref={confirmRef}
                  variant="contained"
                  onClick={async () => {
                    if (isDefined(asset)) {
                      try {
                        setLoading(true);
                        setHasError(false);
                        const desiredWorldPosition =
                          CoordinateManager.getInstance().projectCoordsToLocal(target);
                        const newPosition = desiredWorldPosition.clone().sub(offset);
                        asset.setPosition(newPosition);

                        await saveAssetTransformation(asset);
                        cameraManager().getActiveController()?.focusOnAsset(asset);

                        setShowDialog(false);
                      } catch {
                        setHasError(true);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                >
                  {t('confirm', { ns: 'common' })}
                </Button>
              )}

              <Button ref={closeRef} variant="text" onClick={() => setShowDialog(false)}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { SetAssetCoordinate };
